import { reactive, ref, readonly, watchPostEffect } from 'vue'
import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import { store } from '@remote/store'
import { createTaskScheduler, getEvtNameFromType } from '@remote/utils/update-scheduler'
import { getAppVersion } from '@remote/api/adapter/xos-mqtt'
import { getLastestServerVersions } from '@remote/api/adapter/version-http'
import { logInfo, logError } from '@remote/utils/logger'
import { getErrorMessage, noop } from '@remote/utils/base'
import HeziConfig from '../../hezi.config'
import type { IVersionStore } from '@remote/type/version.d'

function getCurrentDeviceId (): string {
  const deviceId = store.state.curDeviceRunner.params?.target
  return deviceId || ''
}

export interface IStoreState {
  /** 是否存在过期版本（存在即代表需要强制更新） */
  hasDeprecatedVersion: boolean
  /** 是否存在可更新的新版本 */
  hasNewVersion: boolean
  /** 服务发布类型 */
  publishType: 'dev' | 'beta' | 'stable' | string
  /** 服务版本号 */
  version: {
    /** 当前服务版本号 */
    current: number
    /** 最新服务版本号 */
    target: number
  }
  /** 服务版本详情 */
  versionStore: IVersionStore
}

interface IUpdateVersionOptions {
  onSuccess: () => void
  onError: (appid: string, err: Error) => void
  onStart?: () => void
}

export const useHeziVersionStore = defineStore('hz_version', () => {
  const state: IStoreState = reactive({
    hasDeprecatedVersion: false,
    hasNewVersion: false,
    publishType: 'stable',
    version: { current: 0, target: 0 },
    versionStore: {},
  })
  const traceRef = readonly(ref<string>(uuidv4()))
  const updateJobId = ref('')
  const isUpdating = ref(false)
  const deviceId = ref('')

  function checkVersion () {
    const traceId = uuidv4()
    const device_id = getCurrentDeviceId()
    if (!device_id) {
      throw new Error('请先连接设备')
    }
    const deviceInfo = { device_id }
    logInfo('device', {
      traceId,
      category: 'version_controller',
      action: 'check_device_service_version',
      desc: '检测设备服务版本',
      params: { deviceInfo },
    })
    console.log('[checkHeziVersion]检测设备服务版本号:', deviceInfo)
    const version = {
      current: 0,
      target: 0,
    }
    return getAppVersion(traceId).then(response => {
      const { ver: versionCode, versions, runtime, publish_type } = response
      console.log('[checkHeziVersion]当前设备环境:', `runtime=${runtime}, publish_type=${publish_type}`)
      version.current = versionCode
      const versionStore: IVersionStore = {}
      return getLastestServerVersions({ runtime, publish_type }, traceId).then((versionResult) => {
        const config = HeziConfig.verCfg
        const { ver: versionCode, versions: lastVersions } = versionResult
        version.target = versionCode
        console.log('[checkHeziVersion]最低版本配置要求:', config[publish_type])
        console.log('[checkHeziVersion]当前设备服务版本:', versionCode, versions)
        const lastVerLogLabel = Object.keys(lastVersions).reduce((result, appid) => (Object.assign(result, { [appid]: lastVersions[appid].version })), {})
        console.log('[checkHeziVersion]最新服务版本:', versionCode, lastVerLogLabel)
        let hasNewVersion = false
        let hasDeprecatedVersion = false
        Object.keys(versions).forEach(appid => {
          const current = versions[appid]
          const target = lastVersions[appid].version
          const stable = config[publish_type]?.[appid] || current
          const name = lastVersions[appid].name
          if (target && current < target) hasNewVersion = true
          if (stable && stable <= target && stable > current) hasDeprecatedVersion = true
          versionStore[appid] = {
            appid,
            stable,
            current,
            target,
            name,
          }
        })
        logInfo('device', {
          traceId,
          category: 'version_controller',
          action: 'check_device_service_version_succ',
          desc: '检测设备服务版本结果',
          params: { deviceInfo },
          data: {
            hasNewVersion,
            hasDeprecatedVersion,
            versionStore,
            runtime,
            publish_type,
            versionCode,
          },
        })

        state.hasDeprecatedVersion = hasDeprecatedVersion
        state.hasNewVersion = hasNewVersion
        state.versionStore = versionStore
        state.publishType = publish_type
        state.version = version
        const result: IStoreState = { hasNewVersion, hasDeprecatedVersion, versionStore, publishType: publish_type, version }
        console.log('[checkHeziVersion]设备服务版本检测结果:', result)
        return result
      }).catch(err => {
        logError('device', {
          traceId,
          category: 'version_controller',
          action: 'check_device_service_version_fail',
          desc: '获取服务器最新版本信息失败',
          params: { runtime, publish_type },
          error: err,
          errorText: getErrorMessage(err),
        })
        throw err
      })
    }).catch(err => {
      logError('device', {
        traceId,
        category: 'version_controller',
        action: 'check_device_service_version_fail',
        desc: '获取当前设备版本信息失败',
        params: { deviceInfo },
        error: err,
        errorText: getErrorMessage(err),
      })
      throw err
    })
  }

  function updateVersion (options: IUpdateVersionOptions) {
    if (isUpdating.value) return
    const traceId = uuidv4()
    const deviceId = getCurrentDeviceId()
    if (!deviceId) {
      throw new Error('请先连接设备')
    }
    isUpdating.value = true
    updateJobId.value = traceId
    const $scheduler = createTaskScheduler({ deviceId, traceId })

    // 需要更新的服务
    const scheduling: Array<{ appid: string; targetVersion: number }> = []
    const versionStore = state.versionStore
    Object.keys(versionStore).forEach(appid => {
      const service = versionStore[appid]
      const last_version = service.target || 0
      const curr_version = service.current || 0
      if (last_version <= curr_version) return
      const app = { appid, targetVersion: last_version }
      scheduling.push(app)
    })
    
    if (!scheduling.length) {
      return
    }
    const { onError, onSuccess, onStart = noop } = options
    const listeners: Function[] = []
    const removeAllListener = () => {
      listeners.forEach(unlisten => {
        unlisten()
      })
      listeners.length = 0
    }

    const finishedEvtName = getEvtNameFromType('finished')
    const unlistenOnFinish = $scheduler.addEventListener(finishedEvtName, () => {
      isUpdating.value = false
      state.hasDeprecatedVersion = false
      state.hasNewVersion = false
      Object.keys(state.versionStore).forEach(appid => {
        state.versionStore[appid].current = state.versionStore[appid].target
      })
      removeAllListener()
      onSuccess()
      logInfo('device', {
        traceId,
        category: 'version_controller',
        action: 'update_version_finished',
        desc: '服务更新完成',
        params: { deviceId, scheduling },
      })
    })
    listeners.push(unlistenOnFinish)

    const errorEvtName = getEvtNameFromType('error')
    const unlistenOnError = $scheduler.addEventListener(errorEvtName, (data) => {
      isUpdating.value = false
      removeAllListener()
      const { appid, error, type } = data
      onError(appid, error)
      logError('device', {
        traceId,
        category: 'version_controller',
        action: 'update_version',
        desc: '更新服务时出现异常',
        params: { deviceId, appid },
        error,
        errorText: getErrorMessage(error),
        errorType: type,
      })
    })
    listeners.push(unlistenOnError)

    onStart()
    $scheduler.dispatch(scheduling, updateJobId.value)
  }

  watchPostEffect(() => {
    const target = store.state.curDeviceRunner.params?.target
    if (target) {
      deviceId.value = target
    }
  })

  watchPostEffect((onClean) => {
    if (deviceId.value) {
      const traceId = traceRef.value
      const $scheduler = createTaskScheduler({ deviceId: deviceId.value, traceId })
      
      const successEvtName = getEvtNameFromType('success')
      const unlisten = $scheduler.addEventListener(successEvtName, (data) => {
        const { appid } = data
        state.versionStore[appid].current = state.versionStore[appid].target
      })

      onClean(unlisten)
    }
  })

  return { state, checkVersion, isUpdating, updateVersion }
})
